import { Component, OnInit } from '@angular/core';
import { FilterComponentBase } from 'src/app/main/filter-component-base';
import { BindService } from 'src/app/service/bind.service';
import { WorkflowGatewayService } from 'src/app/service/workflow-gateway.service';
import { PagingContainerModel } from 'src/app/model/paging-container.model';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { SearchParamsModel } from 'src/app/model/search-params.model';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-filter-collection',
  templateUrl: './filter-collection.component.html',
  styleUrls: ['./filter-collection.component.scss']
})
export class FilterCollectionComponent extends FilterComponentBase implements OnInit {

  values: AttributeModel[] = [];
  filteredValues: AttributeModel[] = [];
  private searchSubject = new Subject<string>();

  constructor(
    private bindService: BindService,
    private workflowService: WorkflowGatewayService) { 
    super()
  }

  async ngOnInit() {
    if(this.attribute.value == "") {
      this.attribute.value = null
    }
    
    await this.bindService.loadBindings(this.attribute)

    const binding = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == 'binding');

    if (binding && binding.value && binding.value.toLowerCase() === 'processes') {
      const policyIdOpt = binding.options.find(opt => opt.key.toLowerCase() == 'policyid');
      if (policyIdOpt && policyIdOpt.value) {
        this.fetchInitialProcesses(policyIdOpt.value);
      } else {
        this.values = this.getBinding();
        this.filteredValues = this.values;
      }
    } else {
      this.values = this.getBinding();
      this.filteredValues = this.values;
      this.normalizeSelectedOption();
    }

    this.searchSubject.pipe(
      debounceTime(500),
      switchMap(searchText => this.fetchItems(searchText))
    ).subscribe(filtered => {
      this.filteredValues = filtered;
    });
  }

  onSelectChange(newValue: any): void {
    const selectingOption = this.filteredValues
      ?.find(opt => opt.key == this.attribute.value || opt.value == this.attribute.value);

    let selected = this.attribute.attributes
      .find(attr => attr.type?.toLowerCase() == 'filterselectedoption');

    if (selectingOption) {
      if (!selected) {
        selected = {
          key: `${this.attribute.key} Selected`,
          value: selectingOption.value,
          type: 'FilterSelectedOption',
          attributes: [],
          options: []
        } as AttributeModel;

        this.attribute.attributes.push(selected);
      } else {
        selected.value = selectingOption.value;
      }

      selected.attributes = [selectingOption as any];
    } else if (selected) {
      const idx = this.attribute.attributes.indexOf(selected);
      if (idx >= 0) {
        this.attribute.attributes.splice(idx, 1);
      }
      this.attribute.value = null;
    }

    this.emit({
      attribute: this.attribute
    });
  }

  onSearch(event: any): void {
    if (event.term && event.term.length >= 2) {
      this.searchSubject.next(event.term);
    } else {
      this.searchSubject.next('');

      const binding = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == 'binding');

      if (binding && binding.value && binding.value.toLowerCase() === 'processes') {
        const policyIdOpt = binding.options.find(opt => opt.key.toLowerCase() == 'policyid');
        if (policyIdOpt && policyIdOpt.value) {
          this.fetchInitialProcesses(policyIdOpt.value);
          return;
        }
      }

      this.filteredValues = this.values;
    }
  }

  private fetchItems(searchText: string): Observable<AttributeModel[]> {
    if (!searchText) {
      return of(this.values);
    }

    const binding = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == 'binding');

    if (binding && binding.value && binding.value.toLowerCase() === 'processes') {
      const policyIdOpt = binding.options.find(opt => opt.key.toLowerCase() == 'policyid');
      const statusOpt = binding.options.find(opt => opt.key.toLowerCase() == 'status');

      if (!policyIdOpt || !policyIdOpt.value) {
        return of([]);
      }

      const searchParamsModel: SearchParamsModel = {
        policyId: policyIdOpt.value,
        attributes: [],
        status: statusOpt?.value,
        nome: searchText
      } as any;

      const filterSearchParams = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == 'filtersearchparams');

      if (filterSearchParams) {
        filterSearchParams.attributes.forEach(attr => {
          if (attr.key.toLowerCase() == 'status') {
            (searchParamsModel as any).status = attr.value;
          } else {
            searchParamsModel.attributes.push(attr);
          }
        });
      }

      return this.workflowService.getSummary(1, 20, { searchParams: searchParamsModel })
        .pipe(
          map(pagingContainer => {
            const keyMap = binding.options.find(opt => opt.key.toLowerCase() == 'bindvalue')?.value;
            const valueMap = binding.options.find(opt => opt.key.toLowerCase() == 'bindlabel')?.value;

            const getAttr = (attrs: AttributeModel[], k?: string) => {
              if (!k) { return null; }
              return attrs.find(a => a.key?.toLowerCase() === k.toLowerCase());
            };

            return pagingContainer.items.map(item => {
              const mappedKey = getAttr(item.attributes as any, keyMap)?.value || item.id;
              const mappedLabel = getAttr(item.attributes as any, valueMap)?.value
                || (item.attributes[0] && item.attributes[0].value) || item.id;
              return {
                key: mappedKey,
                value: mappedLabel,
                type: 'Text',
                attributes: [],
                options: []
              } as AttributeModel;
            });
          }),
          catchError(() => of([]))
        );
    }

    // Fallback: filtro local para outras fontes (ou quando binding.value está vazio)
    const normalize = (s: string) => (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const needle = normalize(searchText);
    return of(this.values.filter(v => normalize(v.value).includes(needle)));
  }

  private fetchInitialProcesses(policyId: string) {
    const binding = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == 'binding');

    const statusOpt = binding?.options.find(opt => opt.key.toLowerCase() == 'status');

    const searchParamsModel: SearchParamsModel = {
      policyId: policyId,
      attributes: [],
      status: statusOpt?.value
    } as any;

    const filterSearchParams = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == 'filtersearchparams');

    if (filterSearchParams) {
      filterSearchParams.attributes.forEach(attr => {
        if (attr.key.toLowerCase() == 'status') {
          (searchParamsModel as any).status = attr.value;
        } else {
          searchParamsModel.attributes.push(attr);
        }
      });
    }

    this.workflowService
      .getSummary(1, 20, { searchParams: searchParamsModel })
      .subscribe((pagingContainer: PagingContainerModel) => {
        const keyMap = binding?.options.find(opt => opt.key.toLowerCase() == 'bindvalue')?.value;
        const valueMap = binding?.options.find(opt => opt.key.toLowerCase() == 'bindlabel')?.value;

        const getAttr = (attrs: AttributeModel[], k?: string) => {
          if (!k) { return null; }
          return attrs.find(a => a.key?.toLowerCase() === k.toLowerCase());
        };

        this.values = pagingContainer.items.map(item => {
          const mappedKey = getAttr(item.attributes as any, keyMap)?.value || item.id;
          const mappedLabel = getAttr(item.attributes as any, valueMap)?.value
            || (item.attributes[0] && item.attributes[0].value) || item.id;
          return {
            key: mappedKey,
            value: mappedLabel,
            type: 'Text',
            attributes: [],
            options: []
          } as AttributeModel;
        });
        // Garante que a opção já selecionada (vinda de FilterSelectedOption) permaneça visível
        this.ensureSelectedVisible();
        this.filteredValues = this.values;
        this.normalizeSelectedOption();
      });
  }

  private ensureSelectedVisible() {
    const selected = this.attribute.attributes
      .find(attr => attr.type?.toLowerCase() == 'filterselectedoption');

    const option = selected?.attributes?.[0] as any as AttributeModel;
    if (option && !this.values?.some(v => v.key == option.key)) {
      this.values = [...this.values, option];
    }
  }

  private normalizeSelectedOption() {
    if (!this.attribute.value || !this.values?.length) {
      return;
    }

    const selectingOption = this.values
      .find(opt => opt.key == this.attribute.value) ||
      this.values.find(opt => (opt.value || '').toLowerCase() == String(this.attribute.value).toLowerCase());

    if (!selectingOption) {
      return;
    }

    this.attribute.value = selectingOption.key;

    let selected = this.attribute.attributes
      .find(attr => attr.type?.toLowerCase() == 'filterselectedoption');

    if (!selected) {
      selected = {
        key: `${this.attribute.key} Selected`,
        value: selectingOption.value,
        type: 'FilterSelectedOption',
        attributes: [],
        options: []
      } as AttributeModel;
      this.attribute.attributes.push(selected);
    } else {
      selected.value = selectingOption.value;
    }

    selected.attributes = [selectingOption as any];
  }
}
